/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

type ContentStackRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is ContentStackRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const getUid = (value: unknown): string | undefined => {
  if (!isRecord(value) || typeof value.uid !== 'string' || !value.uid) {
    return undefined;
  }

  return value.uid;
};

const getComponentId = (value: unknown): string | undefined => {
  if (!isRecord(value) || typeof value.component_id !== 'string' || !value.component_id) {
    return undefined;
  }

  return value.component_id;
};

const getReferencedValue = (
  block: ContentStackRecord,
  blockName: string,
  getValue: (value: unknown) => string | undefined,
): string | undefined => {
  const directValue = getValue(block);
  if (directValue) {
    return directValue;
  }

  const values = [block[blockName], ...Object.values(block)];

  for (const value of values) {
    if (Array.isArray(value)) {
      const referencedValue = getValue(value[0]);
      if (referencedValue) {
        return referencedValue;
      }
    }
  }

  return undefined;
};

const getReferenceUid = (block: ContentStackRecord, blockName: string): string | undefined => {
  return getReferencedValue(block, blockName, getUid);
};

const toPascalCase = (value: string) =>
  value
    .split('_')
    .filter(Boolean)
    .map((segment) => `${segment.charAt(0).toUpperCase()}${segment.slice(1)}`)
    .join('');

export const getCMSModularBlockId = (
  modularBlock: object | null | undefined,
  blockName: string,
): string | undefined => {
  if (!isRecord(modularBlock)) {
    return undefined;
  }

  const block = modularBlock[blockName];
  if (!isRecord(block)) {
    return undefined;
  }

  const componentId = getReferencedValue(block, blockName, getComponentId);
  if (componentId) {
    return componentId;
  }

  const placementUid = getUid(block._metadata);
  if (!placementUid) {
    return undefined;
  }

  const referenceUid = getReferenceUid(block, blockName);
  const idParts = [`CMS${toPascalCase(blockName)}`];

  if (referenceUid) {
    idParts.push(referenceUid);
  }

  idParts.push(placementUid);

  return idParts.join('-');
};
